import { ui } from "@/components/ui";
import { SurgeryFormFields } from "@/features/events/features/surgeries/components/SurgeryFormFields";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { templateSettingsSchema } from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

type SurgeryDetailsValues = Pick<
	z.infer<typeof templateSettingsSchema>,
	"side" | "orthopaedic_area" | "surgery_option_id"
>;

const SurgeryDetailsPage = () => {
	const navigate = useNavigate();
	const {
		side,
		orthopaedic_area,
		surgery_option_id,
		setSurgerySide,
		setSurgeryOrthopaedicArea,
		setSurgeryOptionId,
	} = useTemplateStore();
	const {
		markStepComplete,
		nextStep,
		setCurrentStep,
		getNextStepRoute,
		getPreviousStepRoute,
		currentStep,
	} = useTemplateNavigationStore();
	const { templateId } = Route.useParams();

	useEffect(() => {
		setCurrentStep("surgery-details");
	}, [setCurrentStep]);

	const form = useForm<SurgeryDetailsValues>({
		resolver: zodResolver(
			templateSettingsSchema.pick({
				side: true,
				orthopaedic_area: true,
				surgery_option_id: true,
			}),
		),
		defaultValues: {
			side: side ?? undefined,
			orthopaedic_area: orthopaedic_area ?? undefined,
			surgery_option_id: surgery_option_id ?? undefined,
		},
	});

	useEffect(() => {
		const subscription = form.watch((value) => {
			setSurgerySide(value?.side ?? null);
			setSurgeryOrthopaedicArea(value?.orthopaedic_area ?? null);
			setSurgeryOptionId(value?.surgery_option_id ?? null);
		});
		return () => subscription.unsubscribe();
	}, [form, setSurgerySide, setSurgeryOrthopaedicArea, setSurgeryOptionId]);

	const handleNext = () => {
		markStepComplete(currentStep);
		nextStep();
		const nextRoute = getNextStepRoute(currentStep, { templateId });
		if (nextRoute) navigate(nextRoute);
	};

	const handlePrevious = () => {
		const prevRoute = getPreviousStepRoute(currentStep, { templateId });
		if (prevRoute) navigate(prevRoute);
	};

	return (
		<ui.Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleNext)}
				className="space-y-6"
			>
				<SurgeryFormFields
					sideFieldName="side"
					orthopaedicAreaFieldName="orthopaedic_area"
					surgeryOptionIdFieldName="surgery_option_id"
				/>

				{/* Navigation Buttons */}
				<div className="flex justify-between gap-4 mt-8">
					<ui.Button
						type="button"
						variant="outline"
						onClick={handlePrevious}
					>
						Previous
					</ui.Button>

					<ui.Button type="submit">Next</ui.Button>
				</div>
			</form>
		</ui.Form>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$templateId/events/surgery/details",
)({
	component: SurgeryDetailsPage,
});
